import { useState } from 'react';
import { cn } from '../lib/cn.js';

export interface AvatarProps {
  name: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeMap = {
  sm: 'size-7 text-xs',
  md: 'size-9 text-sm',
  lg: 'size-11 text-base',
  xl: 'size-14 text-lg',
} as const;

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + second).toUpperCase() || '?';
}

// Stable accent for a name — derived hash, six steel-blue tints
function colorFor(name: string): string {
  const palette = [
    'bg-[#2A4574]',
    'bg-[#3A4257]',
    'bg-[#1B2030]',
    'bg-[#2C3E50]',
    'bg-[#3D4A6B]',
    'bg-[#1F3A5F]',
  ];
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length] ?? palette[0]!;
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const showImg = src && !errored;
  return (
    <span
      className={cn(
        'inline-flex select-none items-center justify-center rounded-full font-semibold text-text-primary border border-border-subtle',
        !showImg && colorFor(name),
        sizeMap[size],
        className,
      )}
      role="img"
      aria-label={name}
    >
      {showImg ? (
        <img
          src={src}
          alt=""
          className="size-full rounded-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}
